import { useEffect, useRef, useState } from "react";
import { api } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { MapPin, Loader2 } from "lucide-react";

export default function AddressAutocomplete({ prefix, onSelect }) {
  const [term, setTerm] = useState("");
  const [results, setResults] = useState([]);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const boxRef = useRef(null);

  useEffect(() => {
    if (!term || term.trim().length < 2) { setResults([]); setLoading(false); return; }
    setLoading(true);
    const ctrl = new AbortController();
    const t = setTimeout(() => {
      api.get("/address/search", { params: { q: term.trim() }, signal: ctrl.signal })
        .then((r) => { setResults(r.data); setOpen(true); })
        .catch(() => setResults([]))
        .finally(() => setLoading(false));
    }, 300);
    return () => { clearTimeout(t); ctrl.abort(); };
  }, [term]);

  useEffect(() => {
    const onClick = (e) => { if (boxRef.current && !boxRef.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, []);

  const choose = async (item) => {
    setOpen(false); setTerm(item.label);
    try {
      const { data } = await api.get(`/address/${item.id}`);
      onSelect(data);
    } catch { /* keep manual entry */ }
  };

  return (
    <div ref={boxRef} className="relative sm:col-span-2">
      <Label className="flex items-center gap-1.5"><MapPin size={13} className="text-accent" /> Find your NZ address</Label>
      <div className="relative">
        <Input data-testid={`${prefix}-addr-autocomplete`} value={term} autoComplete="off"
          onChange={(e) => setTerm(e.target.value)} onFocus={() => results.length && setOpen(true)}
          placeholder="Start typing your address, e.g. 1 Queen Street…" className="pr-9" />
        {loading && <Loader2 size={16} className="absolute right-3 top-1/2 -translate-y-1/2 animate-spin text-muted-foreground" />}
      </div>
      {open && term.trim().length >= 2 && (
        <div data-testid={`${prefix}-addr-suggestions`}
          className="absolute left-0 right-0 top-[68px] z-40 rounded-xl border bg-white shadow-xl overflow-hidden max-h-72 overflow-y-auto">
          {loading && results.length === 0 ? (
            <div className="p-3 text-sm text-muted-foreground">Searching New Zealand addresses…</div>
          ) : results.length === 0 ? (
            <div className="p-3 text-sm text-muted-foreground">No NZ address matches “{term}”. You can type it manually below.</div>
          ) : (
            results.map((a) => (
              <button key={a.id} type="button" data-testid={`${prefix}-addr-option-${a.id}`}
                onClick={() => choose(a)}
                className="w-full flex items-center gap-2.5 p-2.5 text-left text-sm hover:bg-slate-50 border-b last:border-b-0">
                <MapPin size={15} className="text-slate-400 shrink-0" />
                <span className="min-w-0 flex-1">{a.label}</span>
              </button>
            ))
          )}
        </div>
      )}
      <p className="mt-1 text-xs text-muted-foreground">Powered by Addy — New Zealand addresses only. Select a match to auto-fill the fields below.</p>
    </div>
  );
}
